import React from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Section } from "../components/SeoUI";
import { DEMO_IMAGES } from "../data/seoData";
import StylistSection from "../components/StylistSection";
import StyleProfileSection from "../components/StyleProfileSection";
import PricingSection from "../components/PricingSection";
import FAQSection from "../components/FAQSection";
import VirtualTryOnSection from "../components/VirtualTryOnSection";
import useSeoMetadata from "../hooks/useSeoMetadata";

const heroStats = [
  ["100,000+", "PIECES INDEXED"],
  ["50", "CURATED PICKS"],
  ["15s", "PER REFRESH"],
];

const catalogStats = [
  ["100,000+", "PIECES INDEXED"],
  ["50", "CURATED PER FEED"],
  ["15s", "TO REFRESH"],
];

const EvenessPersonalStylingSEOPage = () => {
  useSeoMetadata({
    title: "Personalised AI Fashion Stylist & Virtual Try-On | Eveness",
    description:
      "Discover personal styling powered by your preferences, AI fashion assistance, curated fashion picks and virtual try-on with Eveness.",
    ogTitle: "Eveness | Personalised Styling Built Around You",
  });

  return (
    <main className="min-w-0 bg-white text-[#303030] antialiased selection:bg-[#ffd6c5]">
      <Section className="!pt-12 !pb-10 sm:!pt-16 lg:!pt-20 lg:!pb-14">
        <div className="grid min-w-0 grid-cols-1 items-end gap-8 sm:gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10 xl:gap-14">
          <div className="min-w-0">
            <div className="font-spaceMono text-[11px] uppercase tracking-[.17em] text-[#5C5C5C]">
              <Eyebrow>PERSONALISATION · AI STYLIST</Eyebrow>
            </div>
            <h1 className="max-w-[700px] text-[clamp(30px,4vw,44px)] font-helvena font-medium leading-[1.12] tracking-[-0.02em] text-[#313131] [overflow-wrap:break-word]">
              Styling personalised down to your data.
            </h1>
            <p className="mt-6 max-w-[600px] font-helvena text-[13px] leading-6 text-[#5C5C5C] md:text-[15px]">
              Your Style DNA powers an AI stylist that knows your shape, palette and taste — ask anything, get picks scored to you.
            </p>
            <p className="mt-3 font-helvena text-[13px] text-[#313131] md:text-[15px]">
              The only brand not afraid to say what isn't meant for you.
            </p>
            <div className="mt-8 grid max-w-[420px] grid-cols-3 gap-2 sm:gap-5 lg:mt-11">
              {heroStats.map(([value, label]) => (
                <div key={label} className="min-w-0">
                  <div className="font-helvena text-[16px] leading-tight text-[#313131] sm:text-[20px] lg:text-[22px]">
                    {value}
                  </div>
                  <div className="mt-1 font-spaceMono text-[8px] leading-4 tracking-normal text-[#5C5C5C] sm:text-[9px] xl:text-[10px]">
                    {label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3 sm:mt-8">
              <Link
                to="/"
                className="h-[36px] md:h-[43px] inline-flex items-center justify-center rounded bg-[#F26633] px-6 font-spaceMono md:text-[13px] text-[12px] font-semibold text-white transition hover:bg-[#da4e22]"
              >
                exe_signup
              </Link>
              <Link
                to="https://apps.apple.com/us/app/eveness-ai-fashion-stylist/id6749859788"
                className="h-[36px] md:h-[43px] inline-flex min-h-11 items-center justify-center rounded border border-[#313131] px-5 font-spaceMono md:text-[13px] text-[12px] transition hover:bg-neutral-100"
              >
                Download on App Store ↗
              </Link>
            </div>
          </div>
          <div className="min-w-0 border-y border-neutral-200 py-6 sm:py-8 lg:mb-1">
            <div className="grid grid-cols-3 gap-2 text-center sm:gap-4">
              {catalogStats.map(([value, label]) => (
                <div key={label} className="min-w-0">
                  <div className="font-jetBrainsMono text-[clamp(18px,3vw,42px)] leading-tight tracking-tight text-[#313131]">
                    {value}
                  </div>
                  <div className="mt-2 font-jetBrainsMono text-[8px] leading-4 tracking-normal text-[#5C5C5C] sm:text-[9px] xl:text-[11px]">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-2.5 sm:mt-12 sm:gap-3 md:grid-cols-3 lg:grid-cols-5">
          {DEMO_IMAGES.catalog.map((src, i) => (
            <Link to="/marketplace" key={`${src}-${i}`} className="group block min-w-0">
              <div className="relative aspect-[.82] overflow-hidden rounded-md bg-[#606060]">
                <img
                  src={src}
                  alt={`Illustrative curated fashion look ${i + 1}`}
                  loading={i < 2 ? "eager" : "lazy"}
                  className="h-full w-full object-cover opacity-90 transition duration-300 group-hover:scale-105"
                />
                <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] rounded-sm bg-white px-2 py-1 font-mono text-[9px] text-neutral-900 sm:text-[10px]">
                  Find out match ↗
                </span>
              </div>
              <div className="mt-2 flex min-w-0 flex-wrap justify-between gap-x-2 gap-y-1 text-[10px] sm:text-[11px]">
                <span>Featured look {i + 1}</span>
                <span>Explore</span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* 5. Stylist */}
      <StylistSection />

      {/* 2. Virtual try-on */}
      <VirtualTryOnSection variant="personal" />

      {/* 3. Body profile */}
      <StyleProfileSection />

      {/* 6. Pricing */}
      <PricingSection />

      {/* FAQ and footer */}
      <FAQSection />
    </main>
  );
};

export default EvenessPersonalStylingSEOPage;
